import { useState } from "react";
import { useForm } from "react-hook-form";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Trans, useLingui } from "@lingui/react/macro";
import type { z } from "zod";
import { FilledButton, PlainButton } from "@medoma/components/Button/Button";
import { Text } from "@medoma/components/Text/Text";
import RadioButtons from "@medoma/components/RadioButton/RadioButtons";
import RadioButton from "@medoma/components/RadioButton/RadioButton";
import {
  operationalStatusDictionary,
  operationalStatusSchema,
} from "@medoma/models/equipment";
import Form from "@/components/Form/Form";
import { DescriptionList } from "@/components/DescriptionList/DescriptionList";
import { updateAssetOperationalStatus } from "@/api/Equipment";
import { deducedError, useErrorMessageAlert } from "@/Utils/ErrorUtils";
import styles from "./AssetDetails.module.scss";

type OperationalStatusRowProps = {
  assetId: string;
  operationalStatus: z.infer<typeof operationalStatusSchema>;
  isAllocated: boolean;
};

export const OperationalStatusRow = ({
  assetId,
  operationalStatus,
  isAllocated,
}: OperationalStatusRowProps) => {
  const { t } = useLingui();
  const queryClient = useQueryClient();
  const { displayErrorMessageAlert } = useErrorMessageAlert();
  const [isEditing, setIsEditing] = useState(false);

  const { register, handleSubmit, reset } = useForm<{
    operationalStatus: z.infer<typeof operationalStatusSchema>;
  }>({ defaultValues: { operationalStatus } });

  const { mutate: saveStatus, isPending } = useMutation({
    mutationFn: updateAssetOperationalStatus,
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["equipment"] });
      setIsEditing(false);
    },
    onError: (error) => {
      displayErrorMessageAlert(
        `${t`Kunde inte ändra status.`} ${deducedError(error)}`,
      );
    },
  });

  const cancelEdit = () => {
    reset({ operationalStatus });
    setIsEditing(false);
  };

  const isInactive =
    operationalStatus === operationalStatusSchema.enum.inactive;
  const disableMarkInactive = !isInactive && isAllocated;

  return (
    <>
      <DescriptionList.Term>
        <Trans>Operativ status</Trans>
      </DescriptionList.Term>
      <DescriptionList.Description>
        <div className={styles.fieldWithAction}>
          <Text element="span">
            {t(operationalStatusDictionary[operationalStatus])}
          </Text>
          <div className={styles.actions}>
            {isEditing ? (
              <PlainButton size="small" onClick={cancelEdit}>
                <Trans>Avbryt</Trans>
              </PlainButton>
            ) : (
              <PlainButton
                size="small"
                aria-label={t`Ändra status`}
                onClick={() => setIsEditing(true)}
              >
                <Trans>Ändra</Trans>
              </PlainButton>
            )}
          </div>
          {isEditing ? (
            <div className={styles.editContainer}>
              <Form
                onSubmit={handleSubmit(({ operationalStatus: next }) =>
                  saveStatus({ assetId, operationalStatus: next }),
                )}
              >
                <RadioButtons legend={t`Operativ status`}>
                  <RadioButton
                    label={{ text: t(operationalStatusDictionary.active) }}
                    value={operationalStatusSchema.enum.active}
                    {...register("operationalStatus")}
                  />
                  <RadioButton
                    label={{ text: t(operationalStatusDictionary.inactive) }}
                    value={operationalStatusSchema.enum.inactive}
                    disabled={disableMarkInactive}
                    {...register("operationalStatus")}
                  />
                </RadioButtons>
                {disableMarkInactive ? (
                  <Text element="span" color="faded">
                    <Trans>
                      Kan inte inaktiveras förrän utrustningen har återlämnats
                    </Trans>
                  </Text>
                ) : null}
                <FilledButton type="submit" disabled={isPending}>
                  <Trans>Spara</Trans>
                </FilledButton>
              </Form>
            </div>
          ) : null}
        </div>
      </DescriptionList.Description>
    </>
  );
};
